// INC2-CONTACT: what a person does to one connection (Check, Refresh, Reconnect, Disconnect) and
// to one grant (Revoke), shared by the rows' hover icons and the drawer. The rules are the API's
// (routes/connections.ts, routes/outbox.ts); these only call it and say what happened.
import { newIdempotencyKey } from "@companyos/sdk";
import { toast } from "@companyos/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { Api } from "../../api/client";
import { problemText } from "../settings/problem-text";
import { HEALTH } from "../subscriptions/copy";
import { CONNECT_RETURN_KEY } from "../subscriptions/oauth";
import { type Connection, subscriptionKeys } from "../subscriptions/queries";
import { type Grant, grantKeys } from "./grants";

export function useConnectionActions(api: Api, co: string, slug: string) {
  const qc = useQueryClient();
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: subscriptionKeys.connections(co) });
    void qc.invalidateQueries({ queryKey: grantKeys.all(co) });
  };
  const check = useMutation({
    mutationFn: (c: Connection) =>
      api.client.connections.check({
        params: { co, id: c.id },
        idempotencyKey: newIdempotencyKey(),
      }),
    onSuccess: (r) => {
      refresh();
      toast.add({
        title: r.result.ok
          ? `${r.connection.label} works`
          : `${r.connection.label}: ${HEALTH[r.connection.state]?.label ?? r.connection.state}`,
        type: r.result.ok ? "success" : "error",
      });
    },
    onError: (e) => toast.add({ title: problemText(e, "Not checked."), type: "error" }),
  });
  const renew = useMutation({
    mutationFn: (c: Connection) =>
      api.client.connections.refresh({
        params: { co, id: c.id },
        idempotencyKey: newIdempotencyKey(),
      }),
    onSuccess: (c) => {
      refresh();
      toast.add({
        title:
          c.state === "healthy"
            ? `${c.label}: signed in again`
            : `${c.label}: ${HEALTH[c.state]?.label ?? c.state}`,
        type: c.state === "healthy" ? "success" : "error",
      });
    },
    onError: (e) => toast.add({ title: problemText(e, "Not refreshed."), type: "error" }),
  });
  const reconnect = useMutation({
    mutationFn: (c: Connection) =>
      api.client.connections.oauthStart({
        params: { co, provider: c.provider as "google" | "github" | "slack" },
        query: { scope: c.scope, tiers: c.tiers.join(","), reconnect: c.id },
      }),
    onSuccess: (r) => {
      try {
        sessionStorage.setItem(CONNECT_RETURN_KEY, JSON.stringify({ co, slug }));
      } catch {
        // No storage: the callback page then asks the person to go back by hand.
      }
      window.location.assign(r.authorize_url);
    },
    onError: (e) => toast.add({ title: problemText(e, "Could not reconnect."), type: "error" }),
  });
  const disconnect = useMutation({
    mutationFn: (c: Connection) =>
      api.client.connections.revoke({
        params: { co, id: c.id },
        idempotencyKey: newIdempotencyKey(),
        body: {},
      }),
    onSuccess: (c) => {
      refresh();
      toast.add({ title: `${c.label} disconnected`, type: "success" });
    },
    onError: (e) => toast.add({ title: problemText(e, "Not disconnected."), type: "error" }),
  });
  const revokeGrant = useMutation({
    mutationFn: (g: Grant) =>
      api.client.connectionGrants.revoke({
        params: { co, id: g.connection_id, grant_id: g.id },
        idempotencyKey: newIdempotencyKey(),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: grantKeys.all(co) });
      toast.add({ title: "Access revoked", type: "success" });
    },
    onError: (e) => toast.add({ title: problemText(e, "Not revoked."), type: "error" }),
  });
  return { check, renew, reconnect, disconnect, revokeGrant };
}
